import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { EyeOff, Eye, Search, Truck, MapPin, Plus, DollarSign, Sparkles } from 'lucide-react';
import { formatDateShortAU } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRAILER_TYPES = [
  { k: 'flat_top', label: 'Flat Top' },
  { k: 'drop_deck', label: 'Drop Deck' },
  { k: 'low_loader', label: 'Low Loader' },
  { k: 'tautliner', label: 'Tautliner' },
  { k: 'refrigerated', label: 'Refrigerated' },
  { k: 'tanker', label: 'Tanker' },
  { k: 'side_loader', label: 'Side Loader' },
  { k: 'float', label: 'Float' },
  { k: 'extendable', label: 'Extendable' },
  { k: 'b_double', label: 'B-Double' },
  { k: 'road_train', label: 'Road Train' },
  { k: 'tilt_tray', label: 'Tilt Tray' },
  { k: 'car_carrier', label: 'Car Carrier' },
  { k: 'livestock', label: 'Livestock' },
  { k: 'bulk_tipper', label: 'Bulk Tipper' },
  { k: 'other', label: 'Other' },
];

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

const emptyCreateForm = {
  carrier_email: '',
  fleet_number: '',
  location_suburb: '',
  location_state: 'NSW',
  trailer_types: [],
  max_weight_kg: '',
  available_from: '',
  available_until: '',
  notes: '',
};

export default function AdminTransporterListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | hidden | visible
  const [confirmHide, setConfirmHide] = useState(null); // { listing, reason }
  const [editLocation, setEditLocation] = useState(null); // { listing, suburb, state }
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [relocation, setRelocation] = useState(null); // { listing, suburb, state }
  const [relocSending, setRelocSending] = useState(false);
  const [relocError, setRelocError] = useState('');
  const [relocResult, setRelocResult] = useState(null); // { applied, free, checkout_url }
  const adminEmail = typeof window !== 'undefined' ? (localStorage.getItem('userEmail') || '') : '';

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (filter === 'hidden') params.set('hidden_only', 'true');
      const res = await axios.get(`${API}/admin/transporter-listings?${params.toString()}`);
      let rows = res.data || [];
      if (filter === 'visible') rows = rows.filter(l => !l.hidden_by_admin);
      if (filter === 'name_like') rows = rows.filter(l => l.looks_like_name);
      setListings(rows);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const handleHide = async () => {
    if (!confirmHide) return;
    try {
      await axios.patch(`${API}/admin/transporter-listings/${confirmHide.listing.id}/hide`, {
        hidden: true,
        reason: (confirmHide.reason || '').trim(),
        admin_email: adminEmail,
      });
      setConfirmHide(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to hide listing');
    }
  };

  const handleUnhide = async (listing) => {
    try {
      await axios.patch(`${API}/admin/transporter-listings/${listing.id}/hide`, { hidden: false });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to un-hide listing');
    }
  };

  const handleSaveLocation = async () => {
    if (!editLocation) return;
    const s = (editLocation.suburb || '').trim();
    const st = (editLocation.state || '').trim();
    if (!s || !st) { alert('Suburb and state are required'); return; }
    try {
      await axios.patch(`${API}/admin/transporter-listings/${editLocation.listing.id}/location`, {
        location_suburb: s,
        location_state: st,
      });
      setEditLocation(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update location');
    }
  };

  const toggleTrailerType = (k) =>
    setCreateForm((f) => ({
      ...f,
      trailer_types: f.trailer_types.includes(k)
        ? f.trailer_types.filter((t) => t !== k)
        : [...f.trailer_types, k],
    }));

  const handleCreate = async () => {
    setCreateError('');
    const email = createForm.carrier_email.trim().toLowerCase();
    if (!email) { setCreateError('Carrier email is required'); return; }
    if (!createForm.location_suburb.trim() || !createForm.location_state.trim()) {
      setCreateError('Suburb and state are required'); return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/transporter-listings`, {
        carrier_email: email,
        fleet_number: createForm.fleet_number.trim() || null,
        location_suburb: createForm.location_suburb.trim(),
        location_state: createForm.location_state.trim().toUpperCase(),
        trailer_types: createForm.trailer_types,
        max_weight_kg: createForm.max_weight_kg ? Number(createForm.max_weight_kg) : null,
        available_from: createForm.available_from || null,
        available_until: createForm.available_until || null,
        notes: createForm.notes.trim() || null,
        admin_email: adminEmail,
      });
      setCreateOpen(false);
      setCreateForm(emptyCreateForm);
      load();
    } catch (e) {
      setCreateError(e.response?.data?.detail || 'Failed to add listing');
    } finally {
      setCreating(false);
    }
  };

  const handleSendRelocation = async () => {
    if (!relocation) return;
    setRelocError('');
    const s = (relocation.suburb || '').trim();
    const st = (relocation.state || '').trim();
    if (!s || !st) { setRelocError('Suburb and state are required'); return; }
    setRelocSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/transporter-listings/${relocation.listing.id}/relocation-request`,
        {
          location_suburb: s,
          location_state: st,
          admin_email: adminEmail,
          origin: typeof window !== 'undefined' ? window.location.origin : undefined,
        },
      );
      setRelocResult(res.data);
      load();
    } catch (e) {
      setRelocError(e.response?.data?.detail || 'Failed to send relocation invoice');
    } finally {
      setRelocSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f] flex items-center gap-2"><Truck className="w-6 h-6" /> Transporter Listings — Moderation</h1>
            <p className="text-sm text-gray-500">Hide listings whose fleet number or details violate policy (e.g. personal names in the fleet #).</p>
          </div>
          <Button
            onClick={() => { setCreateForm(emptyCreateForm); setCreateError(''); setCreateOpen(true); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="admin-txl-add-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add listing for carrier (free)
          </Button>
        </div>

        <Card className="p-4 mb-4">
          <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
            <div>
              <Label>Search by fleet #, company, email, suburb</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && load()}
                  className="pl-9"
                  placeholder="e.g. JOHN, Sydney, testadhoc@…"
                  data-testid="admin-txl-search-input"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: 'all', label: 'All' },
                { v: 'visible', label: 'Visible' },
                { v: 'hidden', label: 'Hidden' },
                { v: 'name_like', label: '⚠ Name-like' },
              ].map(opt => (
                <Button
                  key={opt.v}
                  variant={filter === opt.v ? 'default' : 'outline'}
                  size="sm"
                  className={filter === opt.v ? 'bg-[#1e3a5f]' : ''}
                  onClick={() => setFilter(opt.v)}
                  data-testid={`admin-txl-filter-${opt.v}`}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <Button onClick={load} className="bg-[#1e3a5f]" data-testid="admin-txl-search-btn">Search</Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
            <span className="font-semibold uppercase tracking-wider text-gray-500">Legend:</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 border border-emerald-600" /> Paid subscriber — moves free</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500 border border-blue-600" /> Ad-hoc / no sub — $4.99 relocation fee</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-yellow-400 border border-yellow-500" /> Fleet # looks like a name</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500 border border-red-600" /> Hidden</span>
          </div>
        </Card>

        {loading ? (
          <p className="text-center py-10 text-gray-500">Loading…</p>
        ) : listings.length === 0 ? (
          <p className="text-center py-10 text-gray-500">No listings found.</p>
        ) : (
          <div className="space-y-2">
            {listings.map(l => {
              const isSub = !!l.carrier_is_subscriber;
              const borderCls = l.hidden_by_admin
                ? 'border-l-red-500 bg-red-50/40'
                : l.looks_like_name
                  ? 'border-l-yellow-500 bg-yellow-50/40'
                  : isSub
                    ? 'border-l-emerald-500 bg-emerald-50/30'
                    : 'border-l-blue-500 bg-blue-50/20';
              return (
              <Card
                key={l.id}
                className={`p-3 border-l-4 ${borderCls}`}
                data-testid={`admin-txl-row-${l.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {l.fleet_number && (
                        <span
                          className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#1e3a5f] text-white font-mono"
                          data-testid={`admin-txl-fleet-${l.id}`}
                        >
                          #{l.fleet_number}
                        </span>
                      )}
                      {isSub ? (
                        <span
                          className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded font-semibold uppercase flex items-center gap-1"
                          title={l.carrier_has_weekly_sub ? 'Weekly transporter subscriber' : 'Platform subscriber'}
                          data-testid={`admin-txl-sub-badge-${l.id}`}
                        >
                          <Sparkles className="w-3 h-3" /> Subscriber
                        </span>
                      ) : (
                        <span
                          className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded font-semibold uppercase"
                          title="Ad-hoc / non-subscriber — moves are billable"
                          data-testid={`admin-txl-adhoc-badge-${l.id}`}
                        >
                          Ad-hoc
                        </span>
                      )}
                      {l.looks_like_name && !l.hidden_by_admin && (
                        <span
                          className="text-[10px] bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded font-semibold uppercase"
                          title="Fleet # matches a common first name — review before publishing"
                          data-testid={`admin-txl-name-flag-${l.id}`}
                        >
                          ⚠ Name?
                        </span>
                      )}
                      <span className="font-semibold text-[#1e3a5f]">{l.company_name || l.carrier_name || 'Independent Carrier'}</span>
                      <span className="text-xs text-gray-500">— {l.carrier_email}</span>
                      {l.hidden_by_admin && (
                        <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded font-semibold uppercase" data-testid={`admin-txl-hidden-badge-${l.id}`}>Hidden</span>
                      )}
                      {l.status === 'pending_payment' && (
                        <span className="text-[10px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded uppercase">Pending payment</span>
                      )}
                      {l.pending_relocation_id && (
                        <span
                          className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded font-semibold uppercase"
                          title={`Awaiting payment to move to ${l.pending_relocation_target || ''}`}
                          data-testid={`admin-txl-relocation-pending-${l.id}`}
                        >
                          Move pending
                        </span>
                      )}
                      {l.status === 'expired' && (
                        <span className="text-[10px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded uppercase">Expired</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-1 space-x-2">
                      <span>{l.location_suburb}, {l.location_state}</span>
                      {(l.trailer_types || []).length > 0 && <span>· {l.trailer_types.join(', ')}</span>}
                      {l.available_from && <span>· {formatDateShortAU(l.available_from)}{l.available_until ? ` — ${formatDateShortAU(l.available_until)}` : ' → ongoing'}</span>}
                    </div>
                    {l.hidden_by_admin && l.hidden_reason && (
                      <p className="text-xs text-red-700 mt-1 italic">Reason: {l.hidden_reason}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditLocation({ listing: l, suburb: l.location_suburb || '', state: l.location_state || 'NSW' })}
                      className="text-slate-700 border-slate-400 hover:bg-slate-50"
                      title="Free moderation move (no invoice sent)"
                      data-testid={`admin-txl-edit-location-${l.id}`}
                    >
                      <MapPin className="w-3.5 h-3.5 mr-1" /> Edit location (free)
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setRelocError('');
                        setRelocResult(null);
                        setRelocation({ listing: l, suburb: l.location_suburb || '', state: l.location_state || 'NSW' });
                      }}
                      className={isSub ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                      title={isSub ? 'Subscriber — move is free' : 'Ad-hoc — Stripe invoice ($4.99) emailed to carrier'}
                      data-testid={`admin-txl-request-move-${l.id}`}
                    >
                      <DollarSign className="w-3.5 h-3.5 mr-1" /> {isSub ? 'Move (free — sub)' : 'Move (charge $4.99)'}
                    </Button>
                    {l.hidden_by_admin ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnhide(l)}
                        className="text-blue-600 border-blue-500 hover:bg-blue-50"
                        data-testid={`admin-txl-unhide-${l.id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> Un-hide
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => setConfirmHide({ listing: l, reason: '' })}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid={`admin-txl-hide-${l.id}`}
                      >
                        <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
              );
            })}
          </div>
        )}

        {/* Edit location modal */}
        {editLocation && (
          <div
            className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setEditLocation(null)}
            data-testid="admin-txl-location-backdrop"
          >
            <div
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2"><MapPin className="w-5 h-5" /> Update location</h3>
              <p className="text-sm text-gray-600 mb-3">
                <strong>#{editLocation.listing.fleet_number || '—'}</strong> — {editLocation.listing.company_name || editLocation.listing.carrier_email}
              </p>
              <div className="grid grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label>Suburb *</Label>
                  <Input
                    value={editLocation.suburb}
                    onChange={(e) => setEditLocation({ ...editLocation, suburb: e.target.value })}
                    placeholder="e.g. Melbourne"
                    data-testid="admin-txl-location-suburb"
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <select
                    value={editLocation.state}
                    onChange={(e) => setEditLocation({ ...editLocation, state: e.target.value })}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                    data-testid="admin-txl-location-state"
                  >
                    {['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Latitude/longitude will be auto-geocoded from suburb + state.</p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setEditLocation(null)} className="flex-1" data-testid="admin-txl-location-cancel">Cancel</Button>
                <Button onClick={handleSaveLocation} className="flex-1 bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="admin-txl-location-save">Save location</Button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm hide modal */}
        {confirmHide && (
          <div
            className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setConfirmHide(null)}
            data-testid="admin-txl-hide-backdrop"
          >
            <div
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">Hide this listing?</h3>
              <p className="text-sm text-gray-600 mb-3">
                <strong>#{confirmHide.listing.fleet_number || '—'}</strong> — {confirmHide.listing.company_name || confirmHide.listing.carrier_email}<br />
                This will remove it from the public map and Transporters list.
              </p>
              <Label>Reason (optional, saved for audit)</Label>
              <Input
                value={confirmHide.reason}
                onChange={(e) => setConfirmHide({ ...confirmHide, reason: e.target.value })}
                placeholder="e.g. Fleet # contains a personal name"
                data-testid="admin-txl-hide-reason"
              />
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setConfirmHide(null)} className="flex-1" data-testid="admin-txl-hide-cancel">Cancel</Button>
                <Button onClick={handleHide} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="admin-txl-hide-confirm">Hide listing</Button>
              </div>
            </div>
          </div>
        )}
      </main>
      {relocation && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => { if (!relocSending) { setRelocation(null); setRelocResult(null); setRelocError(''); } }}
          data-testid="admin-txl-relocation-backdrop"
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Move truck (carrier-requested)
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              <strong>#{relocation.listing.fleet_number || '—'}</strong> — {relocation.listing.company_name || relocation.listing.carrier_email}
            </p>

            {relocation.listing.carrier_is_subscriber ? (
              <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-2 mb-4">
                <Sparkles className="w-3.5 h-3.5 inline mr-1" /> This carrier is on a paid subscription — location update will be applied <strong>free</strong>, no invoice sent.
              </p>
            ) : (
              <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-2 mb-4">
                Ad-hoc carrier — a <strong>$4.99</strong> Stripe checkout link will be emailed to <strong>{relocation.listing.carrier_email}</strong>. The map only updates once they pay.
              </p>
            )}

            {relocResult ? (
              <div className="space-y-3" data-testid="admin-txl-relocation-result">
                {relocResult.applied ? (
                  <p className="text-sm text-emerald-700">✅ Move applied immediately (subscriber — no charge).</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-700">
                      Invoice sent to <strong>{relocResult.carrier_email}</strong>. The map will update automatically once they pay.
                    </p>
                    <a
                      href={relocResult.checkout_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-blue-600 underline break-all"
                      data-testid="admin-txl-relocation-link"
                    >
                      Copy / open Stripe checkout link
                    </a>
                  </>
                )}
                <Button
                  onClick={() => { setRelocation(null); setRelocResult(null); }}
                  className="w-full bg-[#1e3a5f] hover:bg-[#15304f] text-white"
                  data-testid="admin-txl-relocation-close"
                >
                  Done
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-[2fr_1fr] gap-3">
                  <div>
                    <Label>New suburb *</Label>
                    <Input
                      value={relocation.suburb}
                      onChange={(e) => setRelocation({ ...relocation, suburb: e.target.value })}
                      placeholder="e.g. Brisbane"
                      data-testid="admin-txl-relocation-suburb"
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <select
                      value={relocation.state}
                      onChange={(e) => setRelocation({ ...relocation, state: e.target.value })}
                      className="w-full h-10 border rounded-md px-3 text-sm bg-white"
                      data-testid="admin-txl-relocation-state"
                    >
                      {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                {relocError && (
                  <p className="text-sm text-red-600 mt-2" data-testid="admin-txl-relocation-error">{relocError}</p>
                )}
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setRelocation(null)}
                    className="flex-1"
                    disabled={relocSending}
                    data-testid="admin-txl-relocation-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendRelocation}
                    disabled={relocSending}
                    className={`flex-1 ${relocation.listing.carrier_is_subscriber ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                    data-testid="admin-txl-relocation-submit"
                  >
                    {relocSending
                      ? 'Sending…'
                      : (relocation.listing.carrier_is_subscriber ? 'Apply move (free)' : 'Send $4.99 invoice')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {createOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setCreateOpen(false)}
          data-testid="admin-txl-create-backdrop"
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2">
              <Plus className="w-5 h-5" /> Add transporter listing (comped)
            </h3>
            <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-4 inline-block">
              This listing will be published <strong>free</strong> — no Stripe paywall required for the carrier.
            </p>

            <div className="space-y-4">
              <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label>Carrier email *</Label>
                  <Input
                    value={createForm.carrier_email}
                    onChange={(e) => setCreateForm({ ...createForm, carrier_email: e.target.value })}
                    placeholder="carrier@example.com"
                    data-testid="admin-txl-create-email"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Must belong to an approved carrier account.</p>
                </div>
                <div>
                  <Label>Fleet # (optional)</Label>
                  <Input
                    value={createForm.fleet_number}
                    onChange={(e) => setCreateForm({ ...createForm, fleet_number: e.target.value.toUpperCase().slice(0, 6) })}
                    placeholder="T101"
                    maxLength={6}
                    data-testid="admin-txl-create-fleet"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label>Suburb *</Label>
                  <Input
                    value={createForm.location_suburb}
                    onChange={(e) => setCreateForm({ ...createForm, location_suburb: e.target.value })}
                    placeholder="Melbourne"
                    data-testid="admin-txl-create-suburb"
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <select
                    value={createForm.location_state}
                    onChange={(e) => setCreateForm({ ...createForm, location_state: e.target.value })}
                    className="w-full h-10 border rounded-md px-3 text-sm bg-white"
                    data-testid="admin-txl-create-state"
                  >
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label>Trailer types</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-1">
                  {TRAILER_TYPES.map(t => {
                    const checked = createForm.trailer_types.includes(t.k);
                    return (
                      <label
                        key={t.k}
                        className={`flex items-center gap-1.5 text-xs rounded border px-2 py-1.5 cursor-pointer ${checked ? 'border-[#1e3a5f] bg-blue-50 text-[#1e3a5f] font-semibold' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}
                        data-testid={`admin-txl-create-trailer-${t.k}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTrailerType(t.k)}
                          className="w-3.5 h-3.5 accent-[#1e3a5f]"
                        />
                        {t.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label>Max weight (kg)</Label>
                  <Input
                    type="number"
                    value={createForm.max_weight_kg}
                    onChange={(e) => setCreateForm({ ...createForm, max_weight_kg: e.target.value })}
                    placeholder="e.g. 25000"
                    data-testid="admin-txl-create-max-weight"
                  />
                </div>
                <div>
                  <Label>Available from</Label>
                  <Input
                    type="date"
                    value={createForm.available_from}
                    onChange={(e) => setCreateForm({ ...createForm, available_from: e.target.value })}
                    data-testid="admin-txl-create-from"
                  />
                </div>
                <div>
                  <Label>Available until</Label>
                  <Input
                    type="date"
                    value={createForm.available_until}
                    onChange={(e) => setCreateForm({ ...createForm, available_until: e.target.value })}
                    data-testid="admin-txl-create-until"
                  />
                </div>
              </div>

              <div>
                <Label>Notes (optional)</Label>
                <Input
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  placeholder="e.g. Prefers Sydney → Melbourne backloads"
                  data-testid="admin-txl-create-notes"
                />
              </div>

              {createError && (
                <p className="text-sm text-red-600" data-testid="admin-txl-create-error">{createError}</p>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setCreateOpen(false)}
                  className="flex-1"
                  disabled={creating}
                  data-testid="admin-txl-create-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="admin-txl-create-submit"
                >
                  {creating ? 'Adding…' : 'Add free listing'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
